import { CashoutHubStore, OfferHubStore } from "@state/hubs";

import {
	AutomaticLogoutStore,
	AccountVerificationStore,
} from "@state/stores/account-settings";

import { RestrictionEnum } from "@api-types/restriction/RestrictionEnum";
import RestrictionApiService from "@api-services/restriction/RestrictionApiService";
import BetSlipStore from "@state/bet-slip/BetSlipStore";
import { action, computed, observable } from "mobx";
import { ChatViewStore } from "./chat/ChatViewStore";
import EventSwitcherViewStore from "@state/stores/common/EventSwitcherViewStore";
import { LocalStorageProvider } from "@utils/storage";
import { NotificationFavoritesStore } from "./notification-favorites/NotificationFavoritesStore";
import { NotificationSettingsStore } from "./notification-settings/NotificationSettingsStore";
import { PaymentOptionsStore } from "./payment/payment-options";
import SearchBarViewStore from "@state/stores/common/SearchBarViewStore";
import { SportStatisticHubStore } from "@state/hubs/SportStatisticsHubStore";
import { ThemeStore } from "./components";
import { DiagnosticsProvider } from "@state/providers/diagnosticsProvider";
import PromoOfferViewStore from "@state/stores/offer/home/PromoOfferViewStore";
import BetBuilderPopupStore from "@state/bet-builder/BetBuilderPopupStore";
import { BetBoosterHubStore } from "@state/hubs/BetBoosterHubStore";
import { XtremepushNotificationPermissionPopupStore } from "./crm";
import OnboardingWelcomePopupStore from "./popups/OnboardingWelcomePopupStore";
import FailedLoginsCheckPopupStore from "./popups/FailedLoginsCheckPopupStore";
import { BetBuilderStore } from "@gp/slip";
import {
	CapacitorUpdaterStore,
	CookieConsentStore,
	NotificationStore,
	RealityCheckStore,
	RequiredUserActionsStore,
	SessionExpireStore,
	User24hLockStore,
	UserAuthStore,
	WelcomeStore,
} from "@state/shared";
import { QuickPlayStore } from "@state/quick-play-store";
import CountryChangeStore from "@state/shared/CountryChangeStore";
import HomeStoreManager from "@state/stores/offer/home/HomeStoreManager";
import SearchStore from "@state/stores/offer/search/SearchStore";
import CasinoSearchStore from "./games/CasinoSearchStore";
import GracePeriodStore from "@state/shared/GracePeriodStore";
import RetryHandlerStore from "@state/shared/RetryHandlerStore";
import { MaintenanceStore } from "./maintenance/MaintenanceStore";
import PersonalDataStore from "./personal-data/PersonalDataStore";
import WaitingPeriodStore from "@state/shared/WaitingPeriodStore";
import LogoutPopupStore from "@state/shared/LogoutPopupStore";

export class BaseRootStore {
	@observable isDepositLimitUpdatePopupActive = false;
	@observable restrictionType = RestrictionEnum.None;
	@observable isLocalStorageLoaded = false;
	@observable isStoreInitialized = false;

	accountVerificationStore: AccountVerificationStore;
	automaticLogoutStore: AutomaticLogoutStore;
	betBoosterHubStore: BetBoosterHubStore;
	betBuilderPopupStore: BetBuilderPopupStore;
	betBuilderStore: BetBuilderStore;
	betSlipStore: BetSlipStore;
	capacitorUpdaterStore: CapacitorUpdaterStore;
	cashoutHubStore: CashoutHubStore;
	chatViewStore: ChatViewStore;
	cookieConsentStore: CookieConsentStore;
	countryChangeStore: CountryChangeStore;
	diagnosticsProvider: DiagnosticsProvider;
	eventSwitcherViewStore: EventSwitcherViewStore;
	failedLoginsCheckPopupStore: FailedLoginsCheckPopupStore;
	homeStoreManager: HomeStoreManager;
	localStorageProvider: LocalStorageProvider;
	notificationFavoritesStore: NotificationFavoritesStore;
	notificationSettingsStore: NotificationSettingsStore;
	notificationStore: NotificationStore;
	offerHub: OfferHubStore;
	onboardingWelcomePopupStore: OnboardingWelcomePopupStore;
	paymentOptionsStore: PaymentOptionsStore;
	promoOfferViewStore: PromoOfferViewStore;
	quickPlayStore: QuickPlayStore;
	realityCheckStore: RealityCheckStore;
	requiredUserActionStore: RequiredUserActionsStore;
	searchBarViewStore: SearchBarViewStore;
	searchStore: SearchStore;
	casinoSearchStore: CasinoSearchStore;
	sessionExpireStore: SessionExpireStore;
	sportStatisticsHub: SportStatisticHubStore;
	themeStore: ThemeStore;
	uniqueId: number;
	user24hLockStore: User24hLockStore;
	userAuthStore: UserAuthStore;
	welcomeStore: WelcomeStore;
	xtremepushNotificationPermissionPopupStore: XtremepushNotificationPermissionPopupStore;
	gracePeriodStore: GracePeriodStore;
	retryHandlerStore: RetryHandlerStore;
	maintenanceStore: MaintenanceStore;
	personalDataStore: PersonalDataStore;
	logoutPopupStore: LogoutPopupStore;
	waitingPeriodStore: WaitingPeriodStore;

	constructor() {
		this.localStorageProvider = new LocalStorageProvider();
		this.paymentOptionsStore = new PaymentOptionsStore();
		this.uniqueId = Date.now();
		this.cookieConsentStore = new CookieConsentStore();
		this.searchBarViewStore = new SearchBarViewStore();
		this.failedLoginsCheckPopupStore = new FailedLoginsCheckPopupStore();
		this.eventSwitcherViewStore = new EventSwitcherViewStore();
		this.accountVerificationStore = new AccountVerificationStore();
		this.notificationStore = new NotificationStore();
		this.homeStoreManager = new HomeStoreManager();
		this.promoOfferViewStore = new PromoOfferViewStore();
		this.maintenanceStore = new MaintenanceStore();
	}

	@computed get isIpRestricted() {
		return this.restrictionType === RestrictionEnum.Forbidden;
	}

	@computed get calculatedOfferColumnsNumber() {
		return 1;
	}

	@computed public get isSomePopupActive(): boolean {
		return (
			this.automaticLogoutStore.isActive ||
			this.offerHub.isSessionExpired ||
			this.realityCheckStore.isActive ||
			this.user24hLockStore.isActive
		);
	}

	get hub() {
		return this.offerHub.hub;
	}
	get sportStatHub() {
		// logger.logWarn("Hub is moved to this.rootStore");
		return this.sportStatisticsHub.hub;
	}

	async checkIfIpRestricted() {
		if (
			location.pathname.includes("register/terminal") ||
			location.pathname.includes("register/partner")
		) {
			return;
		}

		const restrictionType =
			await RestrictionApiService.getRestrictionType();
		this.setRestrictionType(restrictionType);
	}

	@action.bound
	setRestrictionType(type: RestrictionEnum) {
		this.restrictionType = type;
	}

	@action.bound
	setLocalStorageLoadStatus(status: boolean) {
		this.isLocalStorageLoaded = status;
	}

	@action.bound
	stopSession() {
		this.sessionExpireStore.clearTimer();
		this.realityCheckStore.clearTimer();
		this.automaticLogoutStore.clearTimer();
		this.realityCheckStore.deactivatePopup();
		this.automaticLogoutStore.deactivatePopup();
	}

	@action.bound
	initClick() {
		this.realityCheckStore.onClick();
		this.sessionExpireStore.onClick();
	}
}
